import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'

export interface EventDetail {
  id: number
  event_time: string
  event_id: number
  message: string
  user_sid?: string
  provider_name?: string
  hostname: string
  record_id?: number | null
  raw?: any
}

export function useEventDetails(
  hostname: string, 
  eventType: 'login_success' | 'login_failure' | 'account_locked' | 'service_crash' | 'app_crash' | 'powershell' | null,
  timeRange: string
) {
  const { session } = useAuth()
  const [events, setEvents] = useState<EventDetail[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!session?.access_token || !hostname || !eventType || !timeRange) {
      return
    }

    const fetchEventDetails = async () => {
      setLoading(true)
      setError(null)

      try {
        console.log(`Fetching event details for ${hostname}, type: ${eventType}, range: ${timeRange}`)
        
        const { data, error } = await supabase.functions.invoke('event-details', {
          body: { hostname, eventType, timeRange },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        })

        if (error) {
          console.error('Error calling event-details function:', error)
          setError('Failed to fetch event details')
          return
        }

        console.log('Received event details:', data)
        const items = (data?.events || []) as EventDetail[]
        const unique = new Map<string, EventDetail>()
        for (const e of items) {
          const k = String(e.record_id ?? `${e.event_id}-${e.event_time}-${(e.message || '').slice(0,128)}`)
          if (!unique.has(k)) unique.set(k, e)
        }
        setEvents(Array.from(unique.values()))
      } catch (err) {
        console.error('Error in useEventDetails:', err)
        setError('Failed to fetch event details')
      } finally {
        setLoading(false)
      }
    }

    fetchEventDetails()
  }, [session?.access_token, hostname, eventType, timeRange])

  return { events, loading, error }
}
